import React from 'react';
import {
  Avatar, Button, Dialog, DialogContent, Typography, PaletteColor, DialogTitle,
  IconButton, DialogActions, Stack, Box, useTheme,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

interface ActionConfirmationDialogProps {
  open: boolean;
  icon: React.ReactElement;
  variant?: 'primary' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  confirmButtonText: string;
  cancelButtonText: string;
  isDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ActionConfirmationDialog: React.FC<ActionConfirmationDialogProps> = ({
  open,
  icon,
  variant = 'primary',
  title,
  message,
  confirmButtonText,
  cancelButtonText,
  isDisabled,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();

  const colorMap: Record<string, PaletteColor> = {
    primary: theme.palette.primary,
    warning: theme.palette.warning,
    info: theme.palette.info,
    success: theme.palette.success,
  };

  const colors = colorMap[variant] || theme.palette.primary;

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={open}
      onClose={onCancel}
      aria-labelledby="action-dialog-title"
      aria-describedby="action-dialog-description"
      sx={{
        '& .MuiDialog-container': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{
              backgroundColor: colors[100],
              color: colors[700],
            }}
          >
            {icon}
          </Avatar>
        </Box>

        <IconButton
          onClick={onCancel}
          sx={{
            color: theme.palette.custom.gray[800],
            mr: '-10px',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack>
          <Typography variant='textLG' 
            fontWeight={600}>{title}</Typography>
          <Typography color="textSecondary" 
            sx={{ mt: 1 }} 
            variant="textSM">
            {message}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Stack direction={{ xs: 'column', md: 'row' }} 
          gap='6px' 
          width='100%'>
          <Button fullWidth 
            variant="secondary" 
            size="md" 
            onClick={onCancel}>
            {cancelButtonText}
          </Button>
          <Button fullWidth 
            variant="primary" 
            size="md" 
            onClick={onConfirm} 
            disabled={isDisabled ?? false}>
            {confirmButtonText}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
